/**
 * MobileFilterBar — the Production Records toolbar on a PHONE
 * (plan-06-mobile.md, lane A). Desktop and tablet keep `FilterRow` inside the
 * pinned panel toolbar; below `md` that toolbar wrapped to two rows and the
 * selects landed under the tabs (IMG_0195).
 *
 * Row 1: the same All / Incomplete / Complete pill tabs (`DayStatusTabs`),
 * scrolling horizontally when the tab labels outgrow the width, and a
 * `Filters` outline button on the right wearing a count of the filters that
 * moved off their defaults (`activeFilterCount`). Tapping it toggles a card
 * under the row holding the SAME select controls `FilterRow` renders (campus
 * only on a multi-campus school), each full width and labelled. State lives
 * in the parent page, exactly as it does for `FilterRow`; only the open /
 * closed state of the card is local.
 */

import { Button } from '@ordo/ui/button'
import { FilterMultiSelect, FilterSelect } from '@ordo/ui/filter-select'
import { SlidersHorizontal } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'

import {
  DATE_WINDOW_OPTIONS,
  type DateWindow,
  type FilterState,
  type Mealtime,
  MEALTIME_OPTIONS,
} from '@/api/production-records'
import { DayStatusTabs } from '@/components/production/filter-row'
import { activeFilterCount } from '@/lib/production-records/mobile-timeline'

export interface MobileFilterBarProps {
  state: FilterState
  onChange: (next: FilterState) => void
  /** Incomplete campus-days in the loaded window (after the mealtime and campus filters): the red count on the Incomplete tab. */
  incompleteCount: number
  /** The campuses the user oversees; the campus select renders only for two or more. */
  campuses?: ReadonlyArray<{ id: string; name: string }>
}

/** A labelled, full-width slot for one of the shared filter pills. */
function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {/* The shared pills size themselves inline; on the phone each one takes the row. */}
      <div className="[&>button]:w-full [&>button]:justify-between [&>button]:py-2 [&>button]:text-sm">{children}</div>
    </div>
  )
}

export function MobileFilterBar({ state, onChange, incompleteCount, campuses = [] }: MobileFilterBarProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const hasCampuses = campuses.length > 1
  const count = activeFilterCount(state, hasCampuses)

  return (
    <div className="space-y-2" data-testid="mobile-filter-bar">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <DayStatusTabs
            value={state.status}
            onChange={(status) => onChange({ ...state, status })}
            incompleteCount={incompleteCount}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((current) => !current)}>
          <SlidersHorizontal aria-hidden />
          Filters
          {count > 0 ? (
            <span
              data-testid="active-filter-count"
              aria-label={`${count} active ${count === 1 ? 'filter' : 'filters'}`}
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold tabular-nums text-primary-foreground">
              {count}
            </span>
          ) : null}
        </Button>
      </div>
      {open ? (
        <div id={panelId} className="space-y-3 rounded-lg border bg-card p-3" data-testid="mobile-filter-panel">
          {hasCampuses ? (
            <FilterField label="Campus">
              <FilterMultiSelect
                values={state.campuses as Set<string>}
                onChange={(next) => onChange({ ...state, campuses: next as ReadonlySet<string> })}
                options={campuses.map((campus) => ({ value: campus.id, label: campus.name }))}
                placeholder="All campuses"
                filterName="campuses"
              />
            </FilterField>
          ) : null}
          <FilterField label="Date window">
            <FilterSelect
              value={state.dateWindow}
              onChange={(v) => onChange({ ...state, dateWindow: v as DateWindow })}
              // Copied at the boundary: the primitive's `options` is mutable, our
              // source of truth is not.
              options={DATE_WINDOW_OPTIONS.map((o) => ({ ...o }))}
            />
          </FilterField>
          <FilterField label="Mealtimes">
            <FilterMultiSelect
              values={state.mealtimes as Set<string>}
              onChange={(next) => onChange({ ...state, mealtimes: next as ReadonlySet<Mealtime> })}
              options={MEALTIME_OPTIONS.map((o) => ({ ...o }))}
              placeholder="All mealtimes"
              filterName="mealtimes"
            />
          </FilterField>
        </div>
      ) : null}
    </div>
  )
}
